import { useEffect, useMemo, useState } from 'react';
import ModalShell from './ModalShell.jsx';
import BenchCard from './BenchCard.jsx';
import BenchArticleDraftPreview from './BenchArticleDraftPreview.jsx';
import { useBench } from '../lib/useBench.js';

const TIERS = [1, 2, 3, 4, 5, 6];

// Bench modal — flat list of bench article options as archive-style rows.
// Click a row to expand it inline for full detail + inline action buttons
// (Draft & add to Today / Hold for Tomorrow / Archive). The "Add to Today"
// path still routes through a stage_4_draft preview (BenchArticleDraftPreview)
// before committing. Tomorrow / Archive remain one-click with a 5-second
// Undo toast at the bottom of the modal.
export default function BenchModal({ isoDate, onClose, onEditionUpdate }) {
  const bench = useBench(isoDate, { onEditionUpdate });
  const [draftingId, setDraftingId] = useState(null); // bench id currently in preview

  const allRows = useMemo(() => {
    const out = [];
    for (const t of TIERS) {
      for (const r of bench.byTier[t] || []) out.push(r);
    }
    return out;
  }, [bench.byTier]);

  const drafting = useMemo(
    () => (draftingId ? allRows.find((r) => String(r._id) === String(draftingId)) : null),
    [draftingId, allRows]
  );

  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!bench.pendingUndo) return;
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [bench.pendingUndo]);

  const undoRemaining = bench.pendingUndo
    ? Math.max(0, Math.ceil((bench.pendingUndo.until - now) / 1000))
    : 0;

  function handlePromoteToday(item) {
    setDraftingId(item._id);
  }

  async function handlePromoteTomorrow(item) {
    try {
      await bench.promote(item._id, 'tomorrow');
    } catch {
      // surfaces via bench.error
    }
  }

  async function handleArchive(item) {
    try {
      await bench.promote(item._id, 'archive');
    } catch {
      // surfaces via bench.error
    }
  }

  async function handleUnpromote(item) {
    try {
      await bench.unpromote(item._id);
    } catch {
      // surfaces via bench.error
    }
  }

  async function commitTodayDraft(article) {
    if (!drafting) return;
    await bench.promote(drafting._id, 'today', { article });
    setDraftingId(null);
  }

  const eyebrow = drafting
    ? `Draft · ${isoDate}`
    : `${allRows.length} options · ${isoDate}`;

  return (
    <ModalShell
      title="Bench"
      eyebrow={eyebrow}
      onClose={onClose}
      widthClass="max-w-6xl"
    >
      {bench.error && (
        <div className="text-[12px] text-hxd-err mb-3">Failed to load: {bench.error}</div>
      )}

      {drafting ? (
        <BenchArticleDraftPreview
          item={drafting}
          isoDate={isoDate}
          onSubmit={commitTodayDraft}
          onBack={() => setDraftingId(null)}
          onCancel={onClose}
          onBudgetExhausted={onClose}
        />
      ) : (
        <>
          {/* Search + filter bar */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <input
              type="search"
              className="hxd-input flex-1 min-w-[200px]"
              placeholder="Search title / source / reason…"
              value={bench.query}
              onChange={(e) => bench.setQuery(e.target.value)}
            />
            <label className="flex items-center gap-1.5 text-[12px] text-hxd-muted">
              <input
                type="checkbox"
                checked={bench.includeArchived}
                onChange={(e) => bench.setIncludeArchived(e.target.checked)}
              />
              Show archived
            </label>
          </div>

          {/* Tier filter chips */}
          <div className="flex flex-wrap gap-1.5 mb-4">
            <FilterChip
              active={bench.tierFilter === null}
              label={`All (${bench.counts.total})`}
              onClick={() => bench.setTierFilter(null)}
            />
            {TIERS.map((t) => {
              const count = bench.counts.byTier?.[t] || 0;
              return (
                <FilterChip
                  key={t}
                  active={bench.tierFilter === t}
                  label={`T${t} (${count})`}
                  onClick={() => bench.setTierFilter(bench.tierFilter === t ? null : t)}
                />
              );
            })}
          </div>

          <div className="flex items-center justify-between mb-3 text-[11px] text-hxd-muted">
            <span>
              {bench.counts.unpromoted} unpromoted · {bench.counts.total} total
            </span>
            {bench.loading && <span>Loading…</span>}
          </div>

          {allRows.length === 0 ? (
            <div className="text-[12px] text-hxd-muted italic py-10 text-center">
              No bench items match. Clear filters or try a broader query.
            </div>
          ) : (
            <div className="space-y-2 pb-4">
              {allRows.map((row) => (
                <BenchCard
                  key={String(row._id)}
                  item={row}
                  onPromoteToday={handlePromoteToday}
                  onPromoteTomorrow={handlePromoteTomorrow}
                  onArchive={handleArchive}
                  onUnpromote={handleUnpromote}
                  busy={bench.loading}
                />
              ))}
            </div>
          )}
        </>
      )}

      {bench.pendingUndo && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-hxd-ink text-white text-[12px] rounded px-4 py-2 flex items-center gap-3 shadow-xl">
          <span>
            Sent to <strong>{bench.pendingUndo.destination}</strong>
          </span>
          <button
            type="button"
            onClick={bench.undo}
            className="font-bold uppercase tracking-display-sm text-hxd-orange hover:text-white"
          >
            Undo ({undoRemaining}s)
          </button>
          <button
            type="button"
            onClick={bench.dismissUndo}
            className="text-white/60 hover:text-white"
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}
    </ModalShell>
  );
}

function FilterChip({ active, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? 'text-[11px] font-bold uppercase tracking-display-sm px-2.5 py-1 rounded-[3px] bg-hxd-orange text-white border border-hxd-orange'
          : 'text-[11px] font-bold uppercase tracking-display-sm px-2.5 py-1 rounded-[3px] bg-white text-hxd-ink border border-hxd-border hover:border-hxd-orange'
      }
    >
      {label}
    </button>
  );
}
